import type { JSX, MouseEvent } from "react";
import { useRef, useState } from "react";
import { useTabs } from "@/context/tabs/TabsContext";
import { Dialog, DialogTrigger } from "../ui/dialog";
import { DeleteTabDialog } from "./DeleteTabDialog";
import "./Tabs.css";
import { DEFAULT_TAB_NUMBER } from "@/context/tabs/defaultTabValues";
import type { TabsState } from "@/context/tabs/types";
import { useAuthService } from "@/hooks/useAuthService/useAuthService";
import { type UserRole, UserRoles } from "@/services/user-auth/user-roles";
import { HoverCardWrapper } from "@/shared/components/HoverCardWrapper";
import { KEY } from "@/shared/constants/keyboard";
import { OfferToUpgrade } from "../../shared/components/OfferToUpgrade";
import { AddDashboardButton } from "./components/AddDashboardButton";
import { isAddButtonAllowed } from "./helpers/isAddButtonAllowed";

const mapOffer: Partial<Record<UserRole, JSX.Element>> = {
	[UserRoles.UNAUTHENTICATED]: (
		<OfferToUpgrade
			offer={"Please create a Free Bronze account"}
			buttonText="Create an Account"
		/>
	),
	[UserRoles.BRONZE]: (
		<OfferToUpgrade
			offer={
				<div className="flex flex-col items-center">
					<p>
						Upgrade to a Silver plan to build a personal Dashboard. For multiple
						Dashboards, upgrade to a Gold plan
					</p>
				</div>
			}
			buttonText="Upgrade"
		/>
	),
	[UserRoles.SILVER]: (
		<OfferToUpgrade
			offer={
				<div className="flex flex-col items-center">
					<p>
						To build and save multiple Dashboards, please upgrade to a Gold plan
					</p>
				</div>
			}
			buttonText="Upgrade to Gold"
		/>
	),
};

export const Tabs = () => {
	const { getTabs, setActiveTab, addTab, closeTab, setTabName, getActiveTab } =
		useTabs();
	const tabs: TabsState = getTabs();
	const [editingId, setEditingId] = useState<string>("");
	const [text, setText] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);
	const authService = useAuthService();
	const role = authService.role || UserRoles.UNAUTHENTICATED;
	const tabsByPlan = tabs.slice(
		0,
		DEFAULT_TAB_NUMBER + authService.customTabsLimit,
	);
	const activeTabId = getActiveTab();

	const disabled = !isAddButtonAllowed(tabsByPlan, authService.customTabsLimit);

	const handleTabClick = (id: string) => {
		setActiveTab(id);
	};

	const handleCloseTab = (e: MouseEvent, id: string): void => {
		e.stopPropagation();
		closeTab(id);
	};

	const handleDoubleClick = (id: string, name: string) => {
		setEditingId(id);
		setText(name);
		setTimeout(() => inputRef.current?.focus(), 0);
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setText(e.target.value);
	};

	const handleBlur = (id: string, name: string) => {
		setEditingId("");
		setTabName(id, name, text);
	};

	const handleKeyDown = (
		e: React.KeyboardEvent<HTMLInputElement>,
		id: string,
		name: string,
	) => {
		if (e.key === "Enter") {
			setEditingId("");
			setTabName(id, name, text);
		}
	};

	return (
		<div className="relative h-[20px] w-full ml-2">
			<div className="absolute flex align-center items-end bottom-[-8px] left-0">
				{tabsByPlan.map((tab) => (
					<div
						key={tab.id}
						className={`tab gap-1 font-medium ${tab.id === activeTabId && "actived"}`}
						onClick={() => handleTabClick(tab.id)}
						onKeyDown={(e) => {
							if (e.key === KEY.ENTER) {
								handleTabClick(tab.id);
							}
						}}
					>
						<input
							ref={editingId === tab.id ? inputRef : null}
							type="text"
							value={text}
							onChange={handleChange}
							onBlur={() => handleBlur(tab.id, tab.name)}
							onKeyDown={(e) => handleKeyDown(e, tab.id, tab.name)}
							className={`border-none outline-none rename-tab ${editingId === tab.id && "open"}`}
						/>

						{editingId !== tab.id && (
							<div
								className={`text-left cursor-pointer ${
									tab.id === activeTabId
										? "text-gold"
										: "text-light-gray hover:text-gold"
								}`}
								onDoubleClick={() => {
									if (!tab.isDefault) {
										handleDoubleClick(tab.id, tab.name);
									}
								}}
							>
								{tab.name}
							</div>
						)}

						{!tab.isDefault && (
							<Dialog>
								<DialogTrigger>
									<div
										className={`text-grey-500 hover:text-white-700 bg-transparent cursor-pointer text-sm leading-[18px] font-bold ${
											tab.id === activeTabId
												? "text-gold"
												: "text-light-gray hover:text-gold"
										}`}
										data-testid="close-tab"
									>
										✕
									</div>
								</DialogTrigger>
								<DeleteTabDialog
									tabName={tab.name}
									handleConfirm={(e) => handleCloseTab(e, tab.id)}
								/>
							</Dialog>
						)}
					</div>
				))}

				<HoverCardWrapper
					data-testid="add-tab-button"
					trigger={<AddDashboardButton addTab={addTab} disabled={disabled} />}
					content={mapOffer[role] ?? null}
					disabled={disabled}
				/>
			</div>
		</div>
	);
};
